/*
 * Discovery set offert au-delà du seuil : une règle du PANIER, pas du tiroir.
 *
 * Trois surfaces modifient le panier (tiroir, page /cart, fiche produit). Tant que la
 * règle vivait dans le tiroir, un changement fait sur la page /cart ne l'appliquait
 * pas : repassé sous le seuil, le panier gardait son cadeau.
 * Chaque surface appelle `MMCartGift.sync()` après son écriture ; la promesse rend
 * `true` si le panier a changé, pour que l'appelant ne re-rende qu'une fois.
 *
 * Ce qui COMPTE pour le seuil vient du réglage « Produits qui comptent pour le seuil »
 * de la section du tiroir, qui reprend la liste de la remise automatique. Le thème
 * comptait tout article non offert, coffret acheté compris : mesuré le 11/09 sur la
 * prévisualisation, 1 parfum + 1 coffret ajoutaient un cadeau que la remise ne voyait
 * pas, donc facturé 40 € (panier 230 € puis 270 €). Liste vide : tout compte.
 *
 * La règle est CONVERGENTE : quel que soit l'état trouvé, elle ramène le panier à
 * exactement une ligne cadeau d'une unité au-dessus du seuil, et aucune en dessous.
 * Deux onglets qui synchronisent en même temps ajoutent chacun un cadeau ; la passe
 * suivante retire le doublon au lieu de le laisser jusqu'au geste d'après.
 *
 * Les appels sont mis en file : chacun relit `/cart.js` après le précédent, jamais
 * un état lu avant une écriture concurrente. Le seuil et la variante viennent des
 * réglages de la section `cart-drawer` ; sans tiroir, aucune règle ne s'applique.
 */
(function () {
  // Chargé par le tiroir ET par la page /cart : une seconde exécution repartirait sur une
  // file vide, donc sur un état lu avant l'écriture en cours.
  if (window.MMCartGift) return;

  const isGift = (item) => item.properties && String(item.properties._gift_set) === 'true';

  function readRule() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return null;
    const threshold = parseInt(drawer.dataset.giftThreshold || '0', 10);
    const variantId = drawer.dataset.giftVariantId || '';
    if (!threshold || !variantId) return null;
    return {
      threshold,
      variantId,
      eligible: (drawer.dataset.giftEligibleProducts || '').split(',').filter(Boolean),
    };
  }

  const countsTowardThreshold = (item, eligible) =>
    !isGift(item) && (eligible.length === 0 || eligible.includes(String(item.product_id)));

  async function apply() {
    const rule = readRule();
    if (!rule) return false;

    const cart = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const gifts = cart.items.filter(isGift);
    // Le coffret n'est jamais vendu : une ligne du coffret sans la marque cadeau (ajout
    // manuel, lien direct vers le panier) est retirée ; seul le cadeau reste.
    const strays = cart.items.filter((item) => !isGift(item) && String(item.variant_id) === String(rule.variantId));
    const eligibleCount = cart.items
      .filter((item) => countsTowardThreshold(item, rule.eligible))
      .reduce((count, item) => count + item.quantity, 0);
    const wanted = eligibleCount >= rule.threshold ? 1 : 0;

    if (strays.length === 0 && gifts.length === wanted && (wanted === 0 || gifts[0].quantity === 1)) return false;

    if (gifts.length === 0 && strays.length === 0) {
      const body = new FormData();
      body.append('id', rule.variantId);
      body.append('quantity', '1');
      body.append('properties[_gift_set]', 'true');
      const response = await fetch(`${routes.cart_add_url}.js`, { method: 'POST', body });
      if (!response.ok) throw new Error(`ajout du cadeau refusé (${response.status})`);
      return true;
    }

    const updates = {};
    strays.forEach((item) => {
      updates[item.key] = 0;
    });
    gifts.forEach((gift, index) => {
      updates[gift.key] = index === 0 ? wanted : 0;
    });
    const response = await fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) throw new Error(`mise à jour du cadeau refusée (${response.status})`);
    return true;
  }

  // Une passe qui a écrit relit le panier : c'est là que se résorbe un cadeau ajouté
  // en double par un autre onglet pendant la première.
  async function converge() {
    if (!(await apply())) return false;
    await apply();
    return true;
  }

  // Un cadeau qui n'arrive pas est exactement l'échec que la cliente doit voir : elle a
  // atteint le seuil et ne reçoit rien. Le journal de la console ne lui parle pas.
  function report(error) {
    console.error('Discovery set : synchronisation du panier impossible', error);
    const box = document.querySelector('cart-drawer [data-mm-error]') || document.getElementById('cart-errors');
    if (box && window.cartStrings) box.textContent = window.cartStrings.error;
  }

  let queue = Promise.resolve();

  window.MMCartGift = {
    sync() {
      const run = queue.then(converge).catch((error) => {
        report(error);
        return false;
      });
      queue = run;
      return run;
    },
  };
})();
